import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader2, MessageSquare, Mail, ClipboardList } from 'lucide-react';
import toast from 'react-hot-toast';
import { listIntakeFormTemplates, type IntakeFormTemplate } from '../../lib/crmIntakeFormTemplates';
import {
  createIntakeInstanceOnly,
  deliverIntakeFormLink,
  deleteIntakeInstanceForBusiness,
  type IntakeInstanceChannel,
} from '../../lib/crmIntakeFormInstances';
import IntakeSendSmsReviewModal from './IntakeSendSmsReviewModal';
import IntakeSendEmailReviewModal from './IntakeSendEmailReviewModal';

interface SendIntakeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  businessName: string;
  opportunityId: number;
  customerId: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  customerFirstName?: string | null;
  onSent: () => void;
}

type ComposeState = {
  mode: IntakeInstanceChannel;
  instanceId: string;
  url: string;
  titleSnapshot: string;
};

export default function SendIntakeFormModal({
  isOpen,
  onClose,
  businessId,
  businessName,
  opportunityId,
  customerId,
  customerPhone,
  customerEmail,
  customerFirstName,
  onSent,
}: SendIntakeFormModalProps) {
  const [templates, setTemplates] = useState<IntakeFormTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [step, setStep] = useState<0 | 1>(0);
  const [compose, setCompose] = useState<ComposeState | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setSelectedTemplateId(null);
      setCompose(null);
      setCreatingDraft(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const list = await listIntakeFormTemplates(businessId);
        if (!cancelled) setTemplates(list);
      } catch (e) {
        console.error(e);
        toast.error('Could not load intake templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, businessId]);

  const phoneOk = Boolean((customerPhone || '').trim());
  const emailOk = Boolean((customerEmail || '').trim());
  const bizName = (businessName || '').trim() || 'Our team';
  const firstName = (customerFirstName || '').trim() || 'there';

  const discardDraftIfAny = async () => {
    if (!compose) return;
    try {
      await deleteIntakeInstanceForBusiness(businessId, compose.instanceId);
    } catch (e) {
      console.error('Failed to discard draft intake instance:', e);
    } finally {
      setCompose(null);
    }
  };

  const handleMainClose = () => {
    if (compose) {
      void discardDraftIfAny().finally(() => onClose());
      return;
    }
    onClose();
  };

  const openComposeForChannel = async (channel: IntakeInstanceChannel) => {
    if (!selectedTemplateId) {
      toast.error('Select a form');
      return;
    }
    if (channel === 'sms' && !phoneOk) {
      toast.error('Add a phone number on the customer to send SMS');
      return;
    }
    if (channel === 'email' && !emailOk) {
      toast.error('Add an email on the customer to send email');
      return;
    }
    setCreatingDraft(true);
    try {
      const draft = await createIntakeInstanceOnly({
        businessId,
        opportunityId,
        customerId,
        templateId: selectedTemplateId,
        channel,
      });
      setCompose({
        mode: channel,
        instanceId: draft.instanceId,
        url: draft.url,
        titleSnapshot: draft.titleSnapshot,
      });
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Could not prepare form link');
    } finally {
      setCreatingDraft(false);
    }
  };

  const handleComposeDismiss = () => {
    void discardDraftIfAny();
  };

  const handleSmsReviewSend = async (message: string) => {
    if (!compose || compose.mode !== 'sms') return;
    try {
      await deliverIntakeFormLink({
        businessId,
        opportunityId,
        customerId,
        channel: 'sms',
        url: compose.url,
        titleSnapshot: compose.titleSnapshot,
        instanceId: compose.instanceId,
        customerPhone,
        customerEmail,
        customerFirstName,
        resent: false,
        smsBody: message,
      });
      setCompose(null);
      toast.success('Form link sent by SMS');
      onSent();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to send SMS');
    }
  };

  const handleEmailReviewSend = async (subject: string, bodyPlain: string) => {
    if (!compose || compose.mode !== 'email') return;
    try {
      await deliverIntakeFormLink({
        businessId,
        opportunityId,
        customerId,
        channel: 'email',
        url: compose.url,
        titleSnapshot: compose.titleSnapshot,
        instanceId: compose.instanceId,
        customerPhone,
        customerEmail,
        customerFirstName,
        resent: false,
        emailSubject: subject,
        emailBodyPlain: bodyPlain,
      });
      setCompose(null);
      toast.success('Form link sent by email');
      onSent();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(e instanceof Error ? e.message : 'Failed to send email');
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div className="fixed inset-0 z-[20010] flex items-end sm:items-center justify-center sm:p-4">
      <button type="button" className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleMainClose} aria-label="Close" />
      <div
        className="relative w-full sm:max-w-md bg-white sm:rounded-2xl shadow-2xl border border-gray-200 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <ClipboardList className="h-4 w-4 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-gray-900 truncate">Send intake form</h2>
              <p className="text-xs text-gray-500">
                {step === 0 ? 'Choose a template' : 'How should we send the link?'}
              </p>
            </div>
          </div>
          <button type="button" onClick={handleMainClose} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-2 text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <span className="text-sm">Loading templates…</span>
            </div>
          ) : step === 0 ? (
            templates.length === 0 ? (
              <p className="text-sm text-gray-600 text-center py-8">
                No intake templates yet. Enable Live Intake in CRM app settings and create a template first.
              </p>
            ) : (
              <ul className="space-y-2">
                {templates.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedTemplateId(t.id)}
                      className={`w-full text-left px-3 py-3 rounded-xl border transition ${
                        selectedTemplateId === t.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      <div className="text-sm font-semibold text-gray-900">{t.title}</div>
                      <div className="text-xs text-gray-500 mt-0.5">Template</div>
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="space-y-3">
              <button
                type="button"
                disabled={!phoneOk || creatingDraft}
                onClick={() => void openComposeForChannel('sms')}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-left"
              >
                {creatingDraft ? (
                  <Loader2 className="h-5 w-5 text-green-600 flex-shrink-0 animate-spin" />
                ) : (
                  <MessageSquare className="h-5 w-5 text-green-600 flex-shrink-0" />
                )}
                <div>
                  <div className="text-sm font-semibold text-gray-900">Send via SMS</div>
                  <div className="text-xs text-gray-500">{phoneOk ? customerPhone : 'No phone on file'}</div>
                </div>
              </button>
              <button
                type="button"
                disabled={!emailOk || creatingDraft}
                onClick={() => void openComposeForChannel('email')}
                className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-left"
              >
                <Mail className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div>
                  <div className="text-sm font-semibold text-gray-900">Send via email</div>
                  <div className="text-xs text-gray-500 truncate">{emailOk ? customerEmail : 'No email on file'}</div>
                </div>
              </button>
              {creatingDraft && (
                <p className="text-xs text-center text-gray-500">Preparing your message…</p>
              )}
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex justify-between gap-2">
          {step === 1 ? (
            <button
              type="button"
              disabled={creatingDraft}
              onClick={() => setStep(0)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-40"
            >
              Back
            </button>
          ) : (
            <button
              type="button"
              onClick={handleMainClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>
          )}
          {step === 0 ? (
            <button
              type="button"
              disabled={!selectedTemplateId || templates.length === 0}
              onClick={() => setStep(1)}
              className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40"
            >
              Next
            </button>
          ) : (
            <span className="text-xs text-gray-400 self-center pr-1" />
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modal, document.body)}
      {compose?.mode === 'sms' && customerPhone && (
        <IntakeSendSmsReviewModal
          isOpen
          onDismiss={handleComposeDismiss}
          onSend={handleSmsReviewSend}
          customerPhone={customerPhone.trim()}
          customerFirstName={firstName}
          businessName={bizName}
          intakeUrl={compose.url}
          formTitle={compose.titleSnapshot}
        />
      )}
      {compose?.mode === 'email' && customerEmail && (
        <IntakeSendEmailReviewModal
          isOpen
          onDismiss={handleComposeDismiss}
          onSend={handleEmailReviewSend}
          customerEmail={customerEmail.trim()}
          customerFirstName={firstName}
          businessName={bizName}
          intakeUrl={compose.url}
          formTitle={compose.titleSnapshot}
        />
      )}
    </>
  );
}
